import { MarkerType, type Edge, type Node } from "@xyflow/react";
import type {
  HealthMapEdge,
  HealthMapLayout,
  HealthMapNode,
  HealthMapNodePosition,
  IntegrationStatus
} from "../../../shared/types";
import { statusLabels, statusTone } from "../../utils/status";

export type HealthMapSourceFilter = "ALL" | "SYNAPSE" | "ACR" | "MARKETING" | "AZURE_MONITOR" | "STORAGE" | "API" | "DASHBOARD";
export type HealthMapStatusFilter = "ALL" | "SUCCEEDED" | "RUNNING" | "FAILED" | "MISSING" | "LATE" | "LONG_RUNNING" | "UNKNOWN";

export type HealthMapLayoutMode = "grid" | "sphere";

export interface HealthMapViewControls {
  sourceFilter: HealthMapSourceFilter;
  statusFilter: HealthMapStatusFilter;
  showLabels: boolean;
  showEdges: boolean;
  unhealthyOnly: boolean;
  layoutMode: HealthMapLayoutMode;
}

export interface HealthMapFlowNodeData extends Record<string, unknown> {
  healthNode: HealthMapNode;
  motion: HealthMapNodeMotion;
  roleClass: string;
  showLabels: boolean;
  statusLabel: string;
  statusTone: string;
  subtitle: string;
  baseScale: number;
  opacity: number;
  sphereMode: boolean;
}

export type HealthMapFlowNode = Node<HealthMapFlowNodeData, "healthMapNode">;

export interface HealthMapFlowEdgeData extends Record<string, unknown> {
  relationshipLabel: string;
  unhealthy: boolean;
  motion: HealthMapEdgeMotion;
}

export type HealthMapFlowEdge = Edge<HealthMapFlowEdgeData>;

export interface HealthMapDragMotion {
  active: boolean;
  intensity: number;
  nodeId: string;
  updatedAt: number;
  vectorX: number;
  vectorY: number;
}

export interface HealthMapNodeMotion {
  active: boolean;
  depth: number;
  intensity: number;
  neighbor: boolean;
  offsetX: number;
  offsetY: number;
  rotate: number;
  rotateX: number;
  rotateY: number;
  settling: boolean;
}

export interface HealthMapEdgeMotion {
  active: boolean;
  connected: boolean;
  intensity: number;
  vectorX: number;
  vectorY: number;
}

export const healthMapSourceFilterOptions: Array<{ value: HealthMapSourceFilter; label: string }> = [
  { value: "ALL", label: "All" },
  { value: "SYNAPSE", label: "Synapse" },
  { value: "ACR", label: "ACR" },
  { value: "MARKETING", label: "Marketing" },
  { value: "AZURE_MONITOR", label: "Azure Monitor" },
  { value: "STORAGE", label: "Storage" },
  { value: "API", label: "API" },
  { value: "DASHBOARD", label: "Dashboard" }
];

export const healthMapStatusFilterOptions: Array<{ value: HealthMapStatusFilter; label: string }> = [
  { value: "ALL", label: "All" },
  { value: "SUCCEEDED", label: "Healthy" },
  { value: "RUNNING", label: "Running" },
  { value: "FAILED", label: "Failed" },
  { value: "MISSING", label: "Missing" },
  { value: "LATE", label: "Late" },
  { value: "LONG_RUNNING", label: "Long Running" },
  { value: "UNKNOWN", label: "Unknown" }
];

const healthMapLayoutStorageKey = "dataIntegrationHealth.healthMap.layout.v1";
const layoutVersion = 1;
const graphWidth = 1280;
const graphHeight = 860;

const statusLayoutAnchors: Record<IntegrationStatus, { x: number; y: number; columns: number; gapX: number; gapY: number }> = {
  SUCCEEDED: { x: 90, y: 90, columns: 4, gapX: 178, gapY: 118 },
  LATE: { x: 660, y: 76, columns: 3, gapX: 228, gapY: 122 },
  RUNNING: { x: 820, y: 268, columns: 2, gapX: 226, gapY: 136 },
  FAILED: { x: 1010, y: 92, columns: 2, gapX: 220, gapY: 126 },
  MISSING: { x: 1010, y: 284, columns: 2, gapX: 220, gapY: 126 },
  LONG_RUNNING: { x: 900, y: 520, columns: 2, gapX: 226, gapY: 132 },
  SCHEDULED: { x: 1110, y: 520, columns: 1, gapX: 0, gapY: 134 },
  CANCELLED: { x: 1120, y: 680, columns: 1, gapX: 0, gapY: 126 },
  UNKNOWN: { x: 640, y: 650, columns: 2, gapX: 210, gapY: 124 }
};

const statusColorByStatus: Record<IntegrationStatus, string> = {
  SCHEDULED: "#9f8cff",
  RUNNING: "#5ec7ef",
  SUCCEEDED: "#4fd08a",
  FAILED: "#ff5f72",
  CANCELLED: "#a7acb2",
  MISSING: "#ff9b54",
  LATE: "#ffd166",
  LONG_RUNNING: "#b68cff",
  UNKNOWN: "#7d8892"
};

const unhealthyStatuses = new Set<IntegrationStatus>(["FAILED", "MISSING", "LATE", "LONG_RUNNING"]);
export const idleHealthMapNodeMotion: HealthMapNodeMotion = {
  active: false,
  depth: 0,
  intensity: 0,
  neighbor: false,
  offsetX: 0,
  offsetY: 0,
  rotate: 0,
  rotateX: 0,
  rotateY: 0,
  settling: false
};
export const idleHealthMapEdgeMotion: HealthMapEdgeMotion = {
  active: false,
  connected: false,
  intensity: 0,
  vectorX: 0,
  vectorY: 0
};
const unhealthyStatusRank: Record<IntegrationStatus, number> = {
  UNKNOWN: 0,
  SCHEDULED: 0,
  SUCCEEDED: 0,
  CANCELLED: 0,
  RUNNING: 0,
  LATE: 1,
  MISSING: 2,
  LONG_RUNNING: 3,
  FAILED: 4
};

export function isUnhealthyStatus(status: IntegrationStatus): boolean {
  return unhealthyStatuses.has(status);
}

export function getStatusColor(status: IntegrationStatus): string {
  return statusColorByStatus[status] || statusColorByStatus.UNKNOWN;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function getNodeSubtitle(node: HealthMapNode): string {
  if (node.subtitle) {
    return node.subtitle;
  }

  if (node.sourceType && node.sourceType !== "PLATFORM") {
    return node.sourceType.replace("_", " ");
  }

  return node.kind;
}

export function getNodeRoleClass(node: HealthMapNode): string {
  if (node.kind === "api") {
    return "role-api";
  }

  if (node.sourceType && node.sourceType !== "PLATFORM" && !node.relatedJobId) {
    return "role-source";
  }

  return "role-job";
}

export function getRelationshipLabel(edge: HealthMapEdge): string {
  if (edge.relationshipType === "writes_raw_payload") {
    return "writes raw payload";
  }
  if (edge.relationshipType === "serving_reads_writes") {
    return "serving reads/writes";
  }
  if (edge.relationshipType === "depends_on") {
    return "depends on";
  }
  if (edge.relationshipType) {
    return edge.relationshipType.replace("_", " ");
  }

  const label = edge.label.toLowerCase();
  if (label.includes("raw") || label.includes("payload")) {
    return "writes raw payload";
  }
  if (label.includes("state") || label.includes("read") || label.includes("write")) {
    return "serving reads/writes";
  }
  if (label.includes("query")) {
    return "queries";
  }
  if (label.includes("alert") || label.includes("webhook")) {
    return "alert";
  }
  if (label.includes("run") || label.includes("task")) {
    return "runs";
  }

  return edge.label || "depends on";
}

export function loadHealthMapLayout(): HealthMapLayout | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    const rawLayout = window.localStorage.getItem(healthMapLayoutStorageKey);
    if (!rawLayout) {
      return undefined;
    }

    const parsed = JSON.parse(rawLayout) as Partial<HealthMapLayout>;
    if (parsed.version !== layoutVersion || !parsed.positions || typeof parsed.positions !== "object") {
      return undefined;
    }

    return parsed as HealthMapLayout;
  } catch {
    return undefined;
  }
}

export function saveHealthMapLayout(layout: HealthMapLayout): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(healthMapLayoutStorageKey, JSON.stringify(layout));
  } catch {
    return;
  }
}

export function clearHealthMapLayout(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(healthMapLayoutStorageKey);
  } catch {
    return;
  }
}

export function createLayoutFromPositions(positions: Record<string, HealthMapNodePosition>): HealthMapLayout {
  return {
    version: layoutVersion,
    positions,
    updatedAt: new Date().toISOString()
  };
}

export function mergeFlowNodePositions(
  currentLayout: HealthMapLayout | undefined,
  flowNodes: Array<Node | HealthMapFlowNode>
): HealthMapLayout {
  const positions: Record<string, HealthMapNodePosition> = {
    ...(currentLayout?.positions || {})
  };

  flowNodes.forEach((node) => {
    positions[node.id] = {
      nodeId: node.id,
      x: Math.round(node.position.x),
      y: Math.round(node.position.y)
    };
  });

  return createLayoutFromPositions(positions);
}

export interface SphereNodeLayout {
  x: number;
  y: number;
  baseScale: number;
  opacity: number;
  /** 3D unit-sphere coordinates (for proximity edge computation) */
  x3: number;
  y3: number;
  z3: number;
}

const SPHERE_CANVAS_CX = 900;
const SPHERE_CANVAS_CY = 480;
const SPHERE_RADIUS_PX = 340;

function roundSpherePixel(value: number): number {
  return Math.round(value * 10) / 10;
}

export function projectSphereNode(x3: number, y3: number, z3: number): { x: number; y: number; baseScale: number; opacity: number } {
  const depthT = (z3 + 1) / 2;
  return {
    x: roundSpherePixel(SPHERE_CANVAS_CX + x3 * SPHERE_RADIUS_PX),
    y: roundSpherePixel(SPHERE_CANVAS_CY + y3 * SPHERE_RADIUS_PX),
    baseScale: 0.48 + depthT * 0.54,
    opacity: 0.22 + depthT * 0.78
  };
}

export function computeSphereLayout(nodes: HealthMapNode[]): Map<string, SphereNodeLayout> {
  const n = nodes.length;
  if (n === 0) return new Map();

  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const result = new Map<string, SphereNodeLayout>();

  nodes.forEach((node, i) => {
    // Fibonacci sphere: evenly distribute points on unit sphere
    const phi = Math.acos(1 - 2 * (i + 0.5) / n);
    const theta = goldenAngle * i;

    const x3 = Math.sin(phi) * Math.cos(theta);
    const y3 = Math.sin(phi) * Math.sin(theta);
    const z3 = Math.cos(phi); // -1 = back, +1 = front

    const { x, y, baseScale, opacity } = projectSphereNode(x3, y3, z3);
    result.set(node.id, { x, y, baseScale, opacity, x3, y3, z3 });
  });

  return result;
}

export function computeSphereProximityEdges(
  nodes: HealthMapNode[],
  sphereLayout: Map<string, SphereNodeLayout>
): HealthMapFlowEdge[] {
  const K = Math.min(5, Math.max(3, Math.round(14 / Math.log2(nodes.length + 2))));
  const edgeSet = new Set<string>();
  const flowEdges: HealthMapFlowEdge[] = [];

  nodes.forEach((node) => {
    const pos = sphereLayout.get(node.id);
    if (!pos) return;

    // Angular distance via dot product on unit sphere
    const ranked = nodes
      .filter((other) => other.id !== node.id)
      .map((other) => {
        const op = sphereLayout.get(other.id);
        if (!op) return { id: other.id, dist: Math.PI };
        const dot = pos.x3 * op.x3 + pos.y3 * op.y3 + pos.z3 * op.z3;
        return { id: other.id, dist: Math.acos(Math.max(-1, Math.min(1, dot))) };
      })
      .sort((a, b) => a.dist - b.dist);

    ranked.slice(0, K).forEach(({ id: otherId }) => {
      const key = [node.id, otherId].sort().join("\x00");
      if (edgeSet.has(key)) return;
      edgeSet.add(key);

      const srcPos = sphereLayout.get(node.id)!;
      const tgtPos = sphereLayout.get(otherId)!;
      const avgDepth = ((srcPos.z3 + 1) / 2 + (tgtPos.z3 + 1) / 2) / 2;
      const strokeOpacity = 0.12 + avgDepth * 0.28;

      flowEdges.push({
        id: `sphere\x00${key}`,
        source: node.id,
        target: otherId,
        type: "staticHealthMapEdge",
        label: "",
        data: {
          relationshipLabel: "",
          unhealthy: false,
          motion: idleHealthMapEdgeMotion
        },
        style: {
          stroke: `rgba(160, 190, 180, ${strokeOpacity.toFixed(2)})`,
          strokeWidth: 0.9 + avgDepth * 0.6
        }
      });
    });
  });

  return flowEdges;
}

export function getDefaultNodePosition(node: HealthMapNode, index: number, statusIndex = index): { x: number; y: number } {
  const anchor = statusLayoutAnchors[node.status] ?? statusLayoutAnchors.UNKNOWN;
  const col = statusIndex % anchor.columns;
  const row = Math.floor(statusIndex / anchor.columns);
  const rowStagger = row % 2 === 1 ? 24 : 0;
  const verticalStagger = ((statusIndex % 3) - 1) * 8;

  return {
    x: Math.min(graphWidth - 140, anchor.x + col * anchor.gapX + rowStagger),
    y: Math.min(graphHeight - 90, anchor.y + row * anchor.gapY + verticalStagger)
  };
}

export function getConnectedNodeIds(edges: HealthMapEdge[], nodeId?: string): Set<string> {
  const connectedNodeIds = new Set<string>();
  if (!nodeId) {
    return connectedNodeIds;
  }

  edges.forEach((edge) => {
    if (edge.source === nodeId) {
      connectedNodeIds.add(edge.target);
    }
    if (edge.target === nodeId) {
      connectedNodeIds.add(edge.source);
    }
  });

  return connectedNodeIds;
}

export function getNodeMotion(
  nodeId: string,
  dragMotion: HealthMapDragMotion | undefined,
  connectedNodeIds: Set<string>
): HealthMapNodeMotion {
  if (!dragMotion) {
    return idleHealthMapNodeMotion;
  }

  const isPrimaryNode = dragMotion.nodeId === nodeId;
  const isNeighborNode = connectedNodeIds.has(nodeId);
  if (!isPrimaryNode && !isNeighborNode) {
    return idleHealthMapNodeMotion;
  }

  if (!dragMotion.active) {
    const releaseFactor = isPrimaryNode ? 0.18 : -0.08;
    const releaseOffsetX = clamp(dragMotion.vectorX * releaseFactor, -10, 10);
    const releaseOffsetY = clamp(dragMotion.vectorY * releaseFactor, -10, 10);

    return {
      ...idleHealthMapNodeMotion,
      depth: isPrimaryNode ? 22 : 10,
      neighbor: isNeighborNode,
      offsetX: releaseOffsetX,
      offsetY: releaseOffsetY,
      rotate: clamp(dragMotion.vectorX * (isPrimaryNode ? 0.14 : -0.05), -5, 5),
      rotateX: clamp(-dragMotion.vectorY * (isPrimaryNode ? 0.14 : 0.05), -6, 6),
      rotateY: clamp(dragMotion.vectorX * (isPrimaryNode ? 0.18 : -0.06), -7, 7),
      settling: isPrimaryNode || isNeighborNode
    };
  }

  const nodeFactor = isPrimaryNode ? 0.58 : -0.18;
  const maxOffset = isPrimaryNode ? 24 : 9;
  const offsetX = clamp(dragMotion.vectorX * nodeFactor, -maxOffset, maxOffset);
  const offsetY = clamp(dragMotion.vectorY * nodeFactor, -maxOffset, maxOffset);

  return {
    active: isPrimaryNode,
    depth: isPrimaryNode ? 34 + dragMotion.intensity * 24 : 14 + dragMotion.intensity * 12,
    intensity: isPrimaryNode ? dragMotion.intensity : dragMotion.intensity * 0.62,
    neighbor: isNeighborNode,
    offsetX,
    offsetY,
    rotate: clamp(dragMotion.vectorX * (isPrimaryNode ? 0.25 : -0.08), -8, 8),
    rotateX: clamp(-dragMotion.vectorY * (isPrimaryNode ? 0.28 : 0.08), -11, 11),
    rotateY: clamp(dragMotion.vectorX * (isPrimaryNode ? 0.34 : -0.1), -13, 13),
    settling: false
  };
}

export function getEdgeMotion(edge: HealthMapEdge, dragMotion?: HealthMapDragMotion): HealthMapEdgeMotion {
  if (!dragMotion) {
    return idleHealthMapEdgeMotion;
  }

  const connected = edge.source === dragMotion.nodeId || edge.target === dragMotion.nodeId;
  if (!connected) {
    return idleHealthMapEdgeMotion;
  }

  return {
    active: dragMotion.active,
    connected: true,
    intensity: dragMotion.active ? dragMotion.intensity : 0,
    vectorX: dragMotion.active ? dragMotion.vectorX : 0,
    vectorY: dragMotion.active ? dragMotion.vectorY : 0
  };
}

export function getConnectedEdgeIds(edges: HealthMapEdge[], nodeId?: string): Set<string> {
  const connectedEdgeIds = new Set<string>();
  if (!nodeId) {
    return connectedEdgeIds;
  }

  edges.forEach((edge) => {
    if (edge.source === nodeId || edge.target === nodeId) {
      connectedEdgeIds.add(edge.id);
    }
  });

  return connectedEdgeIds;
}

export function getMotionForConnectedEdge(dragMotion: HealthMapDragMotion): HealthMapEdgeMotion {
  const releaseIntensity = Math.max(0.55, dragMotion.intensity * 0.8);

  return {
    active: dragMotion.active,
    connected: true,
    intensity: dragMotion.active ? Math.max(0.62, dragMotion.intensity) : releaseIntensity,
    vectorX: dragMotion.active ? dragMotion.vectorX : dragMotion.vectorX * 0.72,
    vectorY: dragMotion.active ? dragMotion.vectorY : dragMotion.vectorY * 0.72
  };
}

export function filterHealthMap(
  nodes: HealthMapNode[],
  edges: HealthMapEdge[],
  controls: HealthMapViewControls
): { nodes: HealthMapNode[]; edges: HealthMapEdge[] } {
  const visibleNodes = nodes.filter((node) => {
    const matchesSource = matchesSourceFilter(node, controls.sourceFilter);
    const matchesStatus = controls.statusFilter === "ALL" || node.status === controls.statusFilter;
    const matchesHealth = !controls.unhealthyOnly || isUnhealthyStatus(node.status);
    return matchesSource && matchesStatus && matchesHealth;
  });

  const visibleNodeIds = new Set(visibleNodes.map((node) => node.id));
  const visibleEdges = edges.filter((edge) => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target));

  return {
    nodes: visibleNodes,
    edges: visibleEdges
  };
}

export function createFlowNodes(
  nodes: HealthMapNode[],
  layout: HealthMapLayout | undefined,
  controls: HealthMapViewControls,
  selectedNodeId?: string
): HealthMapFlowNode[] {
  const isSphere = controls.layoutMode === "sphere";
  const sphereLayout = isSphere ? computeSphereLayout(nodes) : undefined;
  const statusCounts = new Map<IntegrationStatus, number>();

  return nodes.map((node, index) => {
    const statusIndex = statusCounts.get(node.status) ?? 0;
    statusCounts.set(node.status, statusIndex + 1);
    let position: { x: number; y: number };
    let baseScale = 1;
    let opacity = 1;
    let zIndex: number | undefined;

    if (sphereLayout) {
      const sl = sphereLayout.get(node.id);
      position = sl ? { x: sl.x, y: sl.y } : getDefaultNodePosition(node, index, statusIndex);
      baseScale = sl?.baseScale ?? 1;
      opacity = sl?.opacity ?? 1;
      zIndex = sl ? Math.round(((sl.z3 + 1) / 2) * 1000) : undefined;
    } else {
      const storedPosition = layout?.positions[node.id];
      const defaultPosition = getDefaultNodePosition(node, index, statusIndex);
      position = storedPosition ? { x: storedPosition.x, y: storedPosition.y } : defaultPosition;
    }

    return {
      id: node.id,
      type: "healthMapNode",
      position,
      zIndex,
      data: {
        healthNode: node,
        motion: idleHealthMapNodeMotion,
        roleClass: getNodeRoleClass(node),
        showLabels: controls.showLabels,
        statusLabel: statusLabels[node.status],
        statusTone: statusTone[node.status],
        subtitle: getNodeSubtitle(node),
        baseScale,
        opacity,
        sphereMode: isSphere
      },
      draggable: true,
      selected: selectedNodeId === node.id
    };
  });
}

export function createFlowEdges(
  edges: HealthMapEdge[],
  nodeById: Map<string, HealthMapNode>,
  controls: HealthMapViewControls
): HealthMapFlowEdge[] {
  if (!controls.showEdges) {
    return [];
  }

  return edges.map((edge) => {
    const source = nodeById.get(edge.source);
    const target = nodeById.get(edge.target);
    const unhealthy = Boolean(
      isUnhealthyStatus(edge.status) ||
        (source && isUnhealthyStatus(source.status)) ||
        (target && isUnhealthyStatus(target.status))
    );
    const edgeHealthStatus = getEdgeHealthStatus(edge.status, source?.status, target?.status);
    const color = unhealthy ? getStatusColor(edgeHealthStatus) : "rgba(136, 160, 152, 0.58)";

    return {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: "staticHealthMapEdge",
      label: getRelationshipLabel(edge),
      animated: edge.status === "RUNNING",
      data: {
        relationshipLabel: getRelationshipLabel(edge),
        unhealthy,
        motion: idleHealthMapEdgeMotion
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 14,
        height: 14,
        color
      },
      style: {
        stroke: color,
        strokeDasharray: edge.status === "UNKNOWN" ? "7 7" : undefined,
        strokeWidth: unhealthy ? 2.2 : 1.4
      },
      labelBgPadding: [6, 3],
      labelBgBorderRadius: 999,
      labelBgStyle: {
        fill: "rgba(9, 13, 12, 0.88)",
        stroke: unhealthy ? color : "rgba(66, 78, 74, 0.9)"
      },
      labelStyle: {
        fill: unhealthy ? color : "#9aa9a3",
        fontSize: 11,
        fontWeight: 700
      },
      className: unhealthy ? "health-flow-edge health-flow-edge-unhealthy" : "health-flow-edge"
    };
  });
}

function matchesSourceFilter(node: HealthMapNode, filter: HealthMapSourceFilter): boolean {
  if (filter === "ALL") {
    return true;
  }
  if (filter === "STORAGE") {
    return node.kind === "storage";
  }
  if (filter === "API") {
    return node.kind === "api";
  }
  if (filter === "DASHBOARD") {
    return node.kind === "dashboard";
  }

  return node.sourceType === filter;
}

function getEdgeHealthStatus(
  edgeStatus: IntegrationStatus,
  sourceStatus?: IntegrationStatus,
  targetStatus?: IntegrationStatus
): IntegrationStatus {
  return [edgeStatus, sourceStatus, targetStatus]
    .filter((status): status is IntegrationStatus => Boolean(status))
    .sort((left, right) => unhealthyStatusRank[right] - unhealthyStatusRank[left])[0];
}
